import React, { Suspense, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import ErrorBoundary from '~/App/components/ErrorBoundary';
import Header from '~/App/components/Header';
import Loading from '~/App/components/Loading';
import Wrapper from '~/App/components/Wrapper';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import api from '../../api';
import message from '~/utils/message';
import { UserRoleFormValues } from '../../types';
import UserRoleForm from '../../components/UserRoleForm';

const EditUserRoleContent = ({ id }: { id: string }) => {
  const { data } = useSuspenseQuery({
    queryKey: ['fetchUserRole', id],
    queryFn: () => api.getUserRole(id),
    staleTime: 0,
    gcTime: 0,
  });
  const navigate = useNavigate();

  const { isPending, mutate } = useMutation({
    mutationFn: (values: UserRoleFormValues) => api.updateUserRole(id, values),
    retry: false,
    onSuccess: () => {
      message.success('Success edit user role settings');
      navigate('..');
    },
    onError: () => {
      message.error('Fail to edit user role settings');
    },
  });

  const initialFormValues: UserRoleFormValues = useMemo(() => {
    const { user, roles, ...rest } = data;
    return {
      ...rest,
      userId: user.userId,
      roleIds: roles.map(({ roleId }) => roleId),
    };
  }, [data]);

  return (
    <UserRoleForm
      initialValues={initialFormValues}
      loading={isPending}
      onSubmit={mutate}
    />
  );
};

const EditUserRole = () => {
  const { id } = useParams();
  if (!id) return <Navigate to=".." />;
  return (
    <Wrapper>
      <Header title="Edit User Role Settings" />
      <ErrorBoundary>
        <Suspense fallback={<Loading className="h-[400px]" />}>
          <EditUserRoleContent id={id} />
        </Suspense>
      </ErrorBoundary>
    </Wrapper>
  );
};

export default EditUserRole;
